export const ONBOARDING_CHECKLIST_STORAGE_KEY = 'berry-onboarding-checklist-v1';

export type OnboardingStepId =
  | 'accountSetup'
  | 'whatsapp'
  | 'firstConversation'
  | 'company'
  | 'geminiAi'
  | 'users'
  | 'contacts'
  | 'template';

/** שלבים שתמיד נשארים מסומנים כבוצע — לא ניתן להחזיר ל־pending */
export const ONBOARDING_ALWAYS_DONE_STEP_IDS: readonly OnboardingStepId[] = ['accountSetup'];

export type OnboardingStepStatus = 'pending' | 'done' | 'skipped';

export type OnboardingChecklistPersisted = {
  version: 1;
  steps: Record<OnboardingStepId, OnboardingStepStatus>;
  collapsed: boolean;
  /** המשתמש סגר את הווידג'ט (אפשר להציג שוב ממסך הגדרות בעתיד) */
  dismissed: boolean;
};

const defaultSteps = (): Record<OnboardingStepId, OnboardingStepStatus> => ({
  accountSetup: 'done',
  whatsapp: 'pending',
  firstConversation: 'pending',
  company: 'pending',
  geminiAi: 'pending',
  users: 'pending',
  contacts: 'pending',
  template: 'pending',
});

/** מיגרציה מ־messages (שלב ישן בסוף האשף) ל־firstConversation (אחרי וואטסאפ) */
function migrateOnboardingSteps(
  raw: Record<string, OnboardingStepStatus> | undefined,
): Record<OnboardingStepId, OnboardingStepStatus> {
  const merged = { ...defaultSteps(), ...raw };
  const legacyMessages = raw?.messages;
  if (legacyMessages != null && raw?.firstConversation == null) {
    merged.firstConversation = legacyMessages;
  }
  delete (merged as Record<string, unknown>).messages;
  for (const id of ONBOARDING_ALWAYS_DONE_STEP_IDS) {
    merged[id] = 'done';
  }
  return merged;
}

export function getDefaultOnboardingChecklist(): OnboardingChecklistPersisted {
  return {
    version: 1,
    steps: defaultSteps(),
    collapsed: false,
    dismissed: false,
  };
}

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function loadOnboardingChecklist(): OnboardingChecklistPersisted {
  try {
    if (!hasLocalStorage()) {
      return getDefaultOnboardingChecklist();
    }
    const raw = localStorage.getItem(ONBOARDING_CHECKLIST_STORAGE_KEY);
    if (!raw) return getDefaultOnboardingChecklist();
    const parsed = JSON.parse(raw) as Partial<OnboardingChecklistPersisted>;
    if (parsed.version !== 1) return getDefaultOnboardingChecklist();
    const mergedSteps = migrateOnboardingSteps(parsed.steps as Record<string, OnboardingStepStatus> | undefined);
    return {
      ...getDefaultOnboardingChecklist(),
      ...parsed,
      steps: mergedSteps,
    };
  } catch {
    return getDefaultOnboardingChecklist();
  }
}

export function saveOnboardingChecklist(state: OnboardingChecklistPersisted): void {
  try {
    if (!hasLocalStorage()) {
      return;
    }
    localStorage.setItem(ONBOARDING_CHECKLIST_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function countPendingSteps(steps: Record<OnboardingStepId, OnboardingStepStatus>): number {
  return (Object.keys(steps) as OnboardingStepId[]).filter((id) => steps[id] === 'pending').length;
}
